import React, { useEffect, useState } from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { clamp } from "lodash";
import { useTranslation } from "react-i18next";
import { EMAIL_BUILDER_DOM_ID_PREFIX, useStableDomId } from "../../useStableDomId";
import { useEmailBuilderContinuousHistoryGroup } from "../../shell/panel/blockSettings/emailBuilderContinuousHistoryGroup";
import { emailBuilderSettingsAutosaveGestureHandlers } from "../../emailBuilderSettingsTextAutosaveDeferral";

type EmailBuilderSettingsIconNumberFieldProps = {
  label: string;
  value: number;
  min: number;
  max: number;
  icon?: IconDefinition;
  iconFlipX?: boolean;
  disabled?: boolean;
  mixed?: boolean;
  onChange: (value: number, historyGroupId?: string | null) => void;
};

function parseIconNumberInput(raw: string, min: number, max: number): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    return null;
  }

  return clamp(parsed, min, max);
}

export function EmailBuilderSettingsIconNumberField(props: EmailBuilderSettingsIconNumberFieldProps) {
  const { t } = useTranslation();
  const {
    label,
    value,
    min,
    max,
    icon,
    iconFlipX = false,
    disabled = false,
    mixed = false,
    onChange,
  } = props;
  const mixedLabel = mixed ? t("Mixed") : undefined;
  const inputId = useStableDomId(EMAIL_BUILDER_DOM_ID_PREFIX);
  const [inputValue, setInputValue] = useState(mixed && mixedLabel ? mixedLabel : String(value));
  const continuousHistory = useEmailBuilderContinuousHistoryGroup();
  const autosaveGestureHandlers = emailBuilderSettingsAutosaveGestureHandlers(continuousHistory);

  useEffect(() => {
    if (mixed && mixedLabel) {
      setInputValue(mixedLabel);
      return;
    }

    setInputValue(String(value));
  }, [value, mixed, mixedLabel]);

  function commit(next: number) {
    onChange(next, continuousHistory.noteChange());
  }

  function restoreMixedOrValue() {
    if (mixed && mixedLabel) {
      setInputValue(mixedLabel);
      return;
    }

    setInputValue(String(value));
  }

  return (
    <div className="email-builder-settings-icon-number-field">
      <label className="email-builder-settings-icon-number-field__label" htmlFor={inputId}>
        {label}
      </label>
      <div
        className={classNames("email-builder-settings-icon-number-field__input-wrap", {
          "email-builder-settings-icon-number-field__input-wrap--disabled": disabled,
        })}
      >
        {icon ? (
          <span
            className={classNames("email-builder-settings-icon-number-field__icon", {
              "email-builder-settings-icon-number-field__icon--flip-x": iconFlipX,
            })}
            aria-hidden="true"
          >
            <FontAwesomeIcon icon={icon} />
          </span>
        ) : null}
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          className="email-builder-settings-icon-number-field__input"
          data-compact-input
          value={inputValue}
          disabled={disabled}
          onFocus={() => {
            autosaveGestureHandlers.onGestureStart();
            if (mixed && mixedLabel) {
              setInputValue(current => (current === mixedLabel ? "" : current));
            }
          }}
          onBlur={() => {
            autosaveGestureHandlers.onGestureEnd?.();
            const parsed = parseIconNumberInput(inputValue, min, max);
            if (parsed != null) {
              setInputValue(String(parsed));
              commit(parsed);
              return;
            }

            restoreMixedOrValue();
          }}
          onChange={event => {
            const nextInput = event.target.value;
            setInputValue(nextInput);
            const parsed = parseIconNumberInput(nextInput, min, max);
            if (parsed != null) {
              commit(parsed);
            }
          }}
        />
      </div>
    </div>
  );
}
